import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import config from "../../../config";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Chip,
  Pagination,
  Grid,
  Breadcrumbs,
  Divider
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { 
        month: 'short', 
        day: '2-digit', 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
    });
};

const parseJobDetails = (raw) => {
    try {
        let parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (typeof parsed === 'string') parsed = JSON.parse(parsed); // Double-parse in case of nested string
        return parsed || {};
    } catch (e) {
        return {};
    }
};

const LogDetailPanel = ({ log }) => {
    const details = parseJobDetails(log.details);
    const isSync = log.job_name === 'availability_sync';
    const trace = details.activity_trace || [];

    return (
        <Box sx={{ margin: 2, p: 3, backgroundColor: "#ffffff", borderRadius: 2, border: "1px solid #e2e8f0" }}>
            
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="overline" sx={{ fontWeight: 800, color: "#64748b" }}>
                    Execution Summary
                </Typography>
                <Typography variant="caption" sx={{ fontFamily: "monospace", color: "#94a3b8" }}>
                    ID: {log.id}
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ mb: 4 }}>
                {isSync ? (
                    <>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ borderLeft: "3px solid #64748b", pl: 2 }}>
                                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, display: "block", mb: 0.5 }}>STORES PROCESSED</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>{details.stores_count || 0}</Typography>
                                <Typography variant="caption" sx={{ color: "#94a3b8" }}>Total store endpoints analyzed</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ borderLeft: "3px solid #3b82f6", pl: 2 }}>
                                <Typography variant="caption" sx={{ color: "#3b82f6", fontWeight: 700, display: "block", mb: 0.5 }}>ITEMS UPDATED</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>{details.updated || 0}</Typography>
                                <Typography variant="caption" sx={{ color: "#94a3b8" }}>Availability state changes applied</Typography>
                            </Box>
                        </Grid>
                    </>
                ) : (
                    <>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ borderLeft: "3px solid #10b981", pl: 2 }}>
                                <Typography variant="caption" sx={{ color: "#10b981", fontWeight: 700, display: "block", mb: 0.5 }}>PAYMENTS RECOVERED</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>{details.missed_renewals_recovered?.length || 0}</Typography>
                                <Typography variant="caption" sx={{ color: "#94a3b8" }}>Missed gateway webhooks synced</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ borderLeft: "3px solid #f59e0b", pl: 2 }}>
                                <Typography variant="caption" sx={{ color: "#f59e0b", fontWeight: 700, display: "block", mb: 0.5 }}>WARNINGS SENT</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>{details.grace_period_warnings_sent?.length || 0}</Typography>
                                <Typography variant="caption" sx={{ color: "#94a3b8" }}>Emails sent for failed payments</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ borderLeft: "3px solid #ef4444", pl: 2 }}>
                                <Typography variant="caption" sx={{ color: "#ef4444", fontWeight: 700, display: "block", mb: 0.5 }}>SUSPENSIONS</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>{details.subscriptions_suspended?.length || 0}</Typography>
                                <Typography variant="caption" sx={{ color: "#94a3b8" }}>Accounts suspended for failed payments</Typography>
                            </Box>
                        </Grid>
                    </>
                )}
            </Grid>

            <Divider sx={{ mb: 3 }} />

            <Typography variant="overline" sx={{ fontWeight: 800, color: "#64748b", mb: 2, display: "block" }}>
                Activity Trace
            </Typography>
            
            <Box sx={{ 
                p: 2, 
                backgroundColor: "#1e293b", 
                borderRadius: 2, 
                minHeight: "150px", 
                maxHeight: "350px", 
                overflowY: "auto",
                border: "1px solid #0f172a"
            }}>
                {trace.length === 0 ? (
                    <Typography variant="body2" sx={{ color: "#94a3b8", fontStyle: "italic" }}>No trace data recorded.</Typography>
                ) : (
                    trace.map((line, idx) => (
                        <Box key={idx} sx={{ display: "flex", gap: 1.5, mb: 1.5, alignItems: "flex-start" }}>
                            <Typography variant="caption" sx={{ color: "#475569", minWidth: "25px", fontWeight: "bold" }}>
                                {String(idx + 1).padStart(2, '0')}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#e2e8f0", fontFamily: "'IBM Plex Mono', monospace", fontSize: "0.8rem", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                                {line}
                            </Typography>
                        </Box>
                    ))
                )}
                
                {log.error_message && (
                    <Box sx={{ mt: 3, p: 2, backgroundColor: "#7f1d1d", borderRadius: 1, border: "1px solid #991b1b" }}>
                        <Typography variant="caption" sx={{ color: "#fca5a5", fontWeight: 700, display: "block", mb: 1 }}>SYSTEM FAILURE DETECTED:</Typography>
                        <Typography variant="body2" sx={{ color: "#fecaca", fontFamily: "monospace", fontSize: "0.75rem", whiteSpace: "pre-wrap" }}>
                            {log.error_message}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const Row = ({ log }) => {
    const [open, setOpen] = useState(false);
    const details = parseJobDetails(log.details);
    const triggerType = details.trigger_type || "automatic";

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' }, '&:hover': { backgroundColor: '#f8fafc' } }}>
                <TableCell sx={{ width: 50 }}>
                    <IconButton size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{log.job_name === 'availability_sync' ? 'Inventory Sync' : 'Reconciliation'}</TableCell>
                <TableCell>
                    <Chip label={triggerType.toUpperCase()} size="small" sx={{ 
                        backgroundColor: triggerType === 'manual' ? "#e0f2fe" : "#f1f5f9",
                        color: triggerType === 'manual' ? "#0369a1" : "#475569",
                        fontSize: "0.65rem", fontWeight: 700
                    }} />
                </TableCell>
                <TableCell>
                    <Chip label={log.status.toUpperCase()} size="small" sx={{ 
                        backgroundColor: log.status === 'completed' ? '#dcfce7' : log.status === 'running' ? '#fef9c3' : '#fee2e2',
                        color: log.status === 'completed' ? '#166534' : log.status === 'running' ? '#854d0e' : '#991b1b',
                        fontSize: "0.65rem", fontWeight: 700
                    }} />
                </TableCell>
                <TableCell sx={{ color: "#64748b" }}>{formatDate(log.started_at)}</TableCell>
                <TableCell sx={{ color: "#64748b", fontFamily: "monospace" }}>{log.duration_seconds ? `${Number(log.duration_seconds).toFixed(1)}s` : "--"}</TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <LogDetailPanel log={log} />
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
};

const JobLogs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const rowsPerPage = 15;

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${config.CLIENT_API_BASE_URL}/admin-jobs/logs?limit=100`);
            setLogs(res.data);
        } catch (err) {
            console.error("Failed to fetch logs", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, []);

    const handleChangePage = (event, value) => setPage(value);

    if (loading) return <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}><CircularProgress /></Box>;

    const paginatedLogs = logs.slice((page - 1) * rowsPerPage, page * rowsPerPage);

    return (
        <Box sx={{ p: 4, backgroundColor: "#f8fafc", minHeight: "100vh" }}>
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs sx={{ mb: 1 }}>
                    <Link to="/batch-jobs" style={{ textDecoration: 'none', color: '#64748b', fontSize: '0.875rem' }}>Batch Jobs</Link>
                    <Typography color="text.primary" sx={{ fontSize: '0.875rem' }}>Logs</Typography>
                </Breadcrumbs>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-1px" }}>Batch Logs</Typography>
                    <Button startIcon={<RefreshIcon />} variant="outlined" onClick={fetchLogs} sx={{ borderRadius: "8px", textTransform: "none" }}>Refresh Logs</Button>
                </Box>
            </Box>

            <TableContainer component={Paper} sx={{ borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "none", overflow: "hidden" }}>
                <Table>
                    <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                        <TableRow>
                            <TableCell />
                            <TableCell sx={{ fontWeight: 700 }}>Operation Type</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Origin</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Outcome</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Started at</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedLogs.map((log) => <Row key={log.id} log={log} />)}
                        {paginatedLogs.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}>No logs found.</TableCell></TableRow>}
                    </TableBody>
                </Table>
                
                <Box sx={{ p: 3, display: "flex", justifyContent: "center", borderTop: "1px solid #e2e8f0", backgroundColor: "#ffffff" }}>
                    <Pagination 
                        count={Math.ceil(logs.length / rowsPerPage)} 
                        page={page} 
                        onChange={handleChangePage} 
                        color="primary" 
                    />
                </Box>
            </TableContainer>
        </Box>
    );
};

export default JobLogs;
